import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminSidebar from '@/components/AdminSidebar';
import ImageUpload from '@/components/ImageUpload';
import { Plus, Pencil, Trash2, X, Check, Heart, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const empty = { judul: '', deskripsi: '', target: '', foto: '', is_active: true };

export default function AdminProgramDonasi() {
  const [programs, setPrograms] = useState([]);
  const [donasi, setDonasi] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState('program'); // 'program' | 'riwayat'

  useEffect(() => { loadAll(); }, []);

  const loadAll = () => {
    axios.get(`${API}/program-donasi`).then(r => setPrograms(r.data)).catch(() => {});
    axios.get(`${API}/donasi`).then(r => setDonasi(r.data)).catch(() => {});
  };

  const rupiah = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n || 0);

  const totalTerkumpul = donasi.reduce((s, d) => s + (d.nominal || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, target: parseInt(form.target) };
    try {
      if (editId) {
        await axios.put(`${API}/program-donasi/${editId}`, payload);
        toast.success('Program diperbarui');
      } else {
        await axios.post(`${API}/program-donasi`, payload);
        toast.success('Program donasi dibuat');
      }
      setShowForm(false); setEditId(null); setForm(empty);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Gagal'); }
  };

  const handleDeleteProgram = async (id) => {
    if (!window.confirm('Hapus program donasi ini?')) return;
    await axios.delete(`${API}/program-donasi/${id}`);
    toast.success('Program dihapus');
    loadAll();
  };

  const handleDeleteDonasi = async (id) => {
    if (!window.confirm('Hapus riwayat donasi ini?')) return;
    try {
      await axios.delete(`${API}/donasi/${id}`);
      toast.success('Riwayat donasi dihapus');
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Gagal menghapus'); }
  };

  const getProgramName = (program_id) => {
    if (!program_id) return 'Umum';
    const p = programs.find(x => x.id === program_id);
    return p ? p.judul : 'Program tidak aktif';
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white border-b border-gray-100 px-4 md:px-8 py-4 md:py-6 flex items-start md:items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="font-playfair text-2xl font-bold text-emerald-950">Donasi</h1>
            <p className="text-sm text-gray-500">Program fundraising & riwayat donatur pesantren</p>
          </div>
          {activeTab === 'program' && (
            <button
              data-testid="add-program-btn"
              onClick={() => { setForm(empty); setEditId(null); setShowForm(true); }}
              className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2.5 rounded-xl text-sm font-semibold shadow-emerald transition-all"
            >
              <Plus size={16} /> Program Baru
            </button>
          )}
        </div>

        {/* Tab */}
        <div className="px-4 md:px-8 pt-5 pb-0 flex gap-2 border-b border-gray-100 bg-white">
          <button
            data-testid="tab-program"
            onClick={() => setActiveTab('program')}
            className={`px-5 py-2.5 text-sm font-semibold rounded-t-xl border-b-2 transition-all ${
              activeTab === 'program'
                ? 'border-emerald-500 text-emerald-700 bg-emerald-50'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Program Donasi ({programs.length})
          </button>
          <button
            data-testid="tab-riwayat"
            onClick={() => setActiveTab('riwayat')}
            className={`px-5 py-2.5 text-sm font-semibold rounded-t-xl border-b-2 transition-all ${
              activeTab === 'riwayat'
                ? 'border-emerald-500 text-emerald-700 bg-emerald-50'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Riwayat Donatur ({donasi.length})
          </button>
        </div>

        {/* Tab: Program Donasi */}
        {activeTab === 'program' && (
          <div className="p-4 md:p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {programs.map((p, i) => {
              const pct = Math.min(Math.round(((p.terkumpul || 0) / (p.target || 1)) * 100), 100);
              return (
                <motion.div
                  key={p.id}
                  data-testid={`program-item-${p.id}`}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.07 }}
                  className={`bg-white rounded-2xl overflow-hidden shadow-card border border-gray-50 ${!p.is_active ? 'opacity-60' : ''}`}
                >
                  {p.foto ? (
                    <img src={p.foto} alt={p.judul} className="w-full h-40 object-cover" />
                  ) : (
                    <div className="w-full h-40 bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center">
                      <Heart size={36} className="text-emerald-300" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold text-gray-800 text-sm flex-1">{p.judul}</h3>
                      <div className="flex gap-1 ml-2">
                        <button
                          data-testid={`edit-program-${p.id}`}
                          onClick={() => {
                            setForm({ judul: p.judul, deskripsi: p.deskripsi, target: p.target?.toString(), foto: p.foto || '', is_active: p.is_active });
                            setEditId(p.id); setShowForm(true);
                          }}
                          className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          data-testid={`delete-program-${p.id}`}
                          onClick={() => handleDeleteProgram(p.id)}
                          className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 line-clamp-2 mb-3">{p.deskripsi}</p>
                    <div className="mb-2">
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="text-emerald-700 font-semibold">{rupiah(p.terkumpul || 0)}</span>
                        <span className="text-gray-400">dari {rupiah(p.target)}</span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1 text-right">{pct}% tercapai</p>
                    </div>
                    {!p.is_active && (
                      <span className="inline-block text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-lg">Tidak aktif</span>
                    )}
                  </div>
                </motion.div>
              );
            })}
            {programs.length === 0 && (
              <div className="col-span-3 text-center py-16 text-gray-400 text-sm">
                Belum ada program donasi. Buat yang pertama!
              </div>
            )}
          </div>
        )}

        {/* Tab: Riwayat Donatur */}
        {activeTab === 'riwayat' && (
          <div className="p-4 md:p-8">
            {/* Summary */}
            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5 mb-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center flex-shrink-0">
                <TrendingUp size={20} className="text-white" />
              </div>
              <div>
                <p className="text-xs text-emerald-600 font-medium">Total Terkumpul</p>
                <p className="text-2xl font-bold text-emerald-800">{rupiah(totalTerkumpul)}</p>
                <p className="text-xs text-emerald-500 mt-0.5">dari {donasi.length} donatur</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-card overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="bg-emerald-50">
                    {['Donatur', 'Nominal', 'Program', 'Keterangan', 'Tanggal', 'Aksi'].map(h => (
                      <th key={h} className="px-5 py-4 text-left text-xs font-semibold text-emerald-700 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {donasi.map((d, i) => (
                    <motion.tr
                      key={d.id}
                      data-testid={`donasi-row-${d.id}`}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.02 }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-5 py-3.5 text-sm font-semibold text-gray-800">{d.nama_donatur}</td>
                      <td className="px-5 py-3.5 text-sm font-bold text-emerald-700">{rupiah(d.nominal)}</td>
                      <td className="px-5 py-3.5">
                        <span className={`text-xs font-medium px-2 py-1 rounded-lg ${
                          d.program_id ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {getProgramName(d.program_id)}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-xs text-gray-500">{d.keterangan || '-'}</td>
                      <td className="px-5 py-3.5 text-xs text-gray-500">{d.tanggal || '-'}</td>
                      <td className="px-5 py-3.5">
                        <button
                          data-testid={`delete-donasi-btn-${d.id}`}
                          onClick={() => handleDeleteDonasi(d.id)}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Hapus riwayat donasi ini"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </motion.tr>
                  ))}
                  {donasi.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-5 py-12 text-center text-gray-400 text-sm">
                        Belum ada riwayat donasi
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        )}

        {/* ── Form Modal Program ── */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4"
              onClick={e => e.target === e.currentTarget && setShowForm(false)}
            >
              <motion.div
                initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
              >
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                  <h2 className="font-playfair text-xl font-bold text-emerald-950">
                    {editId ? 'Edit Program' : 'Program Baru'}
                  </h2>
                  <button onClick={() => setShowForm(false)} className="p-2 hover:bg-gray-100 rounded-xl">
                    <X size={18} />
                  </button>
                </div>
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Judul Program *</label>
                    <input
                      required
                      value={form.judul}
                      onChange={e => setForm(f => ({ ...f, judul: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Deskripsi *</label>
                    <textarea
                      required
                      value={form.deskripsi}
                      onChange={e => setForm(f => ({ ...f, deskripsi: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Target Dana (IDR) *</label>
                    <input
                      required
                      type="number"
                      min={1000}
                      value={form.target}
                      onChange={e => setForm(f => ({ ...f, target: e.target.value }))}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                    />
                  </div>
                  <ImageUpload value={form.foto} onChange={v => setForm(f => ({ ...f, foto: v }))} label="Foto Program" />
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                      className="w-4 h-4 rounded accent-emerald-500"
                    />
                    <span className="text-sm text-gray-700 font-medium">Tampilkan ke pengguna</span>
                  </label>
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600"
                    >
                      Batal
                    </button>
                    <button
                      data-testid="program-form-submit"
                      type="submit"
                      className="flex-1 py-2.5 bg-emerald-500 text-white rounded-xl text-sm font-semibold shadow-emerald flex items-center justify-center gap-2"
                    >
                      <Check size={15} /> Simpan
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
